/* Customer artwork stays local until the product form is submitted. */
class StudioPersonaliser extends HTMLElement {
  connectedCallback() {
    if(this.started)return;
    this.started=true;
    this.abort=new AbortController();
    this.files=[];
    this.serial=0;
    this.ready=true;
    this.uploadsLoading=false;
    this.variant='';
    this.form=this.closest('form');
    this.$=key=>this.querySelector('[data-sp="'+key+'"]');
    this.canvas=this.querySelector('.sp-art');
    this.ctx=this.canvas.getContext('2d');
    this.variants=JSON.parse(this.$('variants').textContent);
    this.reference='CCS-'+Date.now().toString(36).toUpperCase()+'-'+Math.random().toString(36).slice(2,6).toUpperCase();
    this.$('reference').value=this.reference;
    const signal=this.abort.signal;
    for(const key of ['words','font','theme','ink','paper','size','layout'])this.$(key).addEventListener('input',()=>this.draw(),{signal});
    this.$('photos').addEventListener('change',()=>this.addFiles(),{signal});
    this.$('download').addEventListener('click',()=>{const a=document.createElement('a');a.download=this.reference+'-layout.jpg';a.href=this.canvas.toDataURL('image/jpeg',.9);a.click();},{signal});
    this.$('view').addEventListener('click',()=>this.toggle3D(),{signal});
    this.$('left').addEventListener('click',()=>this.viewer?.rotate(-.3),{signal});
    this.$('right').addEventListener('click',()=>this.viewer?.rotate(.3),{signal});
    this.$('ready').addEventListener('click',()=>{if(!this.ready||this.uploadsLoading){this.error('Please wait for your photos and preview to finish preparing.');return;}if(this.files.length>this.maxPhotos){this.error('Remove extra photos for this frame size before adding it to your bag.');return;}this.error('');this.$('status').textContent='Design '+this.reference+' is ready. Review your selected product options, then use Add to bag below.';this.form.querySelector('[type="submit"]')?.scrollIntoView({block:'center',behavior:'smooth'});},{signal});
    this.form.addEventListener('submit',e=>{this.syncVariant();if(!this.ready||this.uploadsLoading||this.files.length>this.maxPhotos){e.preventDefault();e.stopImmediatePropagation();this.error((!this.ready||this.uploadsLoading)?'Please wait for the design preview to finish preparing.':'This size allows '+this.maxPhotos+' photos. Remove extra photos before adding to your bag.');}},{capture:true,signal});
    this.syncVariant();
    this.timer=setInterval(()=>this.syncVariant(),400);
    this.draw();
    window.addEventListener('pagehide',()=>this.dispose(),{once:true,signal});
  }
  disconnectedCallback(){this.dispose();this.started=false;}
  dispose(){
    this.abort?.abort();clearInterval(this.timer);clearTimeout(this.saveTimer);this.serial++;
    this.viewer?.dispose();this.files?.forEach(x=>URL.revokeObjectURL(x.url));
  }
  error(message){const e=this.querySelector('.sp-error');e.textContent=message;e.hidden=!message;}
  syncVariant(){
    const id=this.form.querySelector('[name="id"]')?.value||'';
    if(id===this.variant)return;
    this.variant=id;
    const v=this.variants.find(x=>String(x.id)===id);
    this.variantTitle=v?.title||'';
    const map={'4 × 6':4,'6 × 8':6,'8 × 10':8,'10 × 12':12};
    this.maxPhotos=this.dataset.kind==='frame'?(Object.entries(map).find(([s])=>this.variantTitle.includes(s))?.[1]||4):4;
    this.$('limit').textContent='Up to '+this.maxPhotos+' photos for this option. JPG, PNG or WebP; up to 5 MB each, 25 MB total. Add files in the order you want them shown.';
    this.$('photos').setCustomValidity(this.files.length>this.maxPhotos?'Remove extra photos for the selected frame size.':'');
    if(this.files.length>this.maxPhotos)this.error('This frame size allows '+this.maxPhotos+' photos. Remove extras below; your selected photos have been kept.');
    this.viewer?.setVariant(this.variantTitle);
    this.draw();
  }
  async addFiles(){
    if(this.uploadsLoading)return;
    const chosen=[...this.$('photos').files];this.$('photos').value='';
    if(!chosen.length)return;
    if(this.files.length+chosen.length>this.maxPhotos){this.error('Please select at most '+this.maxPhotos+' photos in total.');return;}
    if(chosen.some(f=>!['image/jpeg','image/png','image/webp'].includes(f.type)||f.size>5*1024*1024)){this.error('Use JPG, PNG or WebP files up to 5 MB each. Convert HEIC photos to JPG first.');return;}
    if([...this.files.map(f=>f.file),...chosen].reduce((n,f)=>n+f.size,0)>25*1024*1024){this.error('Please keep the combined photo size under 25 MB.');return;}
    this.ready=false;this.uploadsLoading=true;this.$('photos').disabled=true;
    const batch=[];
    try{
      for(const file of chosen){
        const url=URL.createObjectURL(file),image=new Image();
        try{await new Promise((resolve,reject)=>{image.onload=resolve;image.onerror=reject;image.src=url;});}
        catch(e){URL.revokeObjectURL(url);throw e;}
        if(image.naturalWidth*image.naturalHeight>45000000){URL.revokeObjectURL(url);throw Error('large');}
        batch.push({file,url,image});
      }
      if(!this.isConnected){batch.forEach(x=>URL.revokeObjectURL(x.url));return;}
      this.files.push(...batch);this.renderFiles();this.error('');this.draw();
    }catch(e){batch.forEach(x=>URL.revokeObjectURL(x.url));this.error('One of these images could not be read. Try a smaller JPG or PNG.');this.ready=true;}
    finally{this.uploadsLoading=false;this.$('photos').disabled=false;}
  }
  renderFiles(){
    const list=this.querySelector('.sp-photo-list'),fields=this.querySelector('.sp-upload-fields');
    list.replaceChildren();fields.replaceChildren();
    try{
      this.files.forEach((entry,i)=>{
        const input=document.createElement('input');input.type='file';input.name='properties[Photo '+(i+1)+']';
        const dt=new DataTransfer();dt.items.add(entry.file);input.files=dt.files;fields.append(input);
        const wrap=document.createElement('div');wrap.className='sp-photo';
        const img=document.createElement('img');img.src=entry.url;img.alt='Uploaded photo '+(i+1);
        const label=document.createElement('span');label.textContent=(i+1)+'. '+entry.file.name;
        const remove=document.createElement('button');remove.type='button';remove.textContent='Remove '+(i+1);
        remove.addEventListener('click',()=>{URL.revokeObjectURL(entry.url);this.files.splice(i,1);this.renderFiles();this.$('photos').setCustomValidity(this.files.length>this.maxPhotos?'Remove extra photos':'');this.error('');this.draw();});
        wrap.append(img,label,remove);list.append(wrap);
      });
    }catch(e){this.ready=false;this.error('This browser cannot attach the selected images. Please use a recent browser or contact the studio before ordering.');return false;}
    return true;
  }
  draw(){
    if(!this.ctx)return;
    const c=this.ctx,w=this.canvas.width,h=this.canvas.height;
    c.fillStyle=this.$('paper').value;c.fillRect(0,0,w,h);
    c.fillStyle=this.$('ink').value;c.strokeStyle=this.$('ink').value;
    const theme=this.$('theme').value;
    if(theme!=='Minimal'&&theme!=='Custom'){
      c.globalAlpha=.15;
      for(let i=0;i<10;i++){const x=50+i*103,y=i%2?48:h-48;c.beginPath();if(theme==='Floral'){for(let j=0;j<6;j++){const a=j*Math.PI/3;c.moveTo(x+10*Math.cos(a),y+10*Math.sin(a));c.arc(x+12*Math.cos(a),y+12*Math.sin(a),8,0,Math.PI*2);}}else{c.arc(x,y,theme==='Romantic'?10:5+(i%3)*4,0,Math.PI*2);}c.fill();}
      c.globalAlpha=1;
    }
    c.lineWidth=3;c.strokeRect(35,35,w-70,h-70);
    const count=this.files.length,text=this.$('words').value.trim(),spaceText=text?245:70;
    const top=85,bottom=h-spaceText-35,photos=this.files.map(f=>f.image);
    if(count){
      let cols=count===1?1:count<=4?2:count<=9?3:4;
      const rows=Math.ceil(count/cols),gap=18,cellW=(w-140-gap*(cols-1))/cols,cellH=(bottom-top-gap*(rows-1))/rows;
      photos.forEach((image,i)=>{
        let x=70+i%cols*(cellW+gap),y=top+Math.floor(i/cols)*(cellH+gap),cw=cellW,ch=cellH;
        if(this.$('layout').value==='Hero'&&count>1){
          if(i===0){x=70;y=top;cw=w-140;ch=(bottom-top)*.56;}
          else{const n=count-1,cols2=Math.min(n,4),rows2=Math.ceil(n/cols2);cw=(w-140-gap*(cols2-1))/cols2;ch=((bottom-top)*.4-gap*(rows2-1))/rows2;x=70+(i-1)%cols2*(cw+gap);y=top+(bottom-top)*.6+Math.floor((i-1)/cols2)*(ch+gap);}
        }
        const ratio=Math.min(cw/image.naturalWidth,ch/image.naturalHeight),iw=image.naturalWidth*ratio,ih=image.naturalHeight*ratio;
        c.drawImage(image,x+(cw-iw)/2,y+(ch-ih)/2,iw,ih);
      });
    }else{
      c.globalAlpha=.35;c.textAlign='center';c.font='38px Arial';c.fillText('Your photo or design',w/2,h*.4);c.globalAlpha=1;
    }
    if(text){
      c.textAlign='center';c.textBaseline='middle';
      const fontSize={Small:40,Medium:54,Large:68}[this.$('size').value];
      let size=fontSize,lines;
      do{
        c.font=size+'px '+this.$('font').value;
        lines=[];for(const para of text.split('\n')){
          let line='';for(const word of para.split(/\s+/)){let candidate=line?line+' '+word:word;if(c.measureText(candidate).width>w-160&&line){lines.push(line);line=word;}else line=candidate;}if(line)lines.push(line);
        }
        if(lines.length*size*1.2<=195&&lines.every(x=>c.measureText(x).width<=w-120))break;size-=2;
      }while(size>14);
      lines.forEach((line,i)=>c.fillText(line,w/2,h-150+(i-(lines.length-1)/2)*size*1.2,w-120));
    }
    this.viewer?.refresh();
    this.ready=false;const revision=++this.serial;
    clearTimeout(this.saveTimer);
    this.saveTimer=setTimeout(()=>this.canvas.toBlob(blob=>{
      if(revision!==this.serial||!this.isConnected)return;
      try{
        let hidden=this.form.querySelector('input[data-design-preview]');
        if(!hidden){hidden=document.createElement('input');hidden.type='file';hidden.hidden=true;hidden.dataset.designPreview='';hidden.name='properties[Design preview]';this.querySelector('.sp-upload-fields').append(hidden);}
        const dt=new DataTransfer();dt.items.add(new File([blob],this.reference+'-layout.jpg',{type:'image/jpeg'}));hidden.files=dt.files;
        this.ready=true;
      }catch(e){this.error('Preview attachment could not be prepared. Please use a recent browser or contact the studio.');}
    },'image/jpeg',.85),180);
  }
  async toggle3D(){
    if(this.viewer){const active=this.querySelector('.sp-three').hidden;this.show3D(active);if(active)this.viewer.refresh();return;}
    const button=this.$('view');button.disabled=true;button.textContent='Loading 3D…';
    try{
      const module=await import(this.dataset.viewerUrl||window.studioViewerUrl);
      this.viewer=await module.createStudioViewer(this.querySelector('.sp-three'),this.canvas,this.dataset.kind,this.variantTitle);
      this.show3D(true);
    }catch(e){this.error('3D could not load on this device. Your print layout and uploads still work.');button.textContent='Try 3D preview again';}
    finally{button.disabled=false;}
  }
  show3D(active){
    this.querySelector('.sp-three').hidden=!active;this.canvas.hidden=active;
    this.$('left').hidden=!active;this.$('right').hidden=!active;
    this.$('view').textContent=active?'Show print layout':'Open 3D preview';
    if(active)this.viewer?.resize();
  }
}
if(!customElements.get('studio-personaliser'))customElements.define('studio-personaliser',StudioPersonaliser);
